import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useHits, useInstantSearch, useSortBy } from 'react-instantsearch';

import type { PrintablePlayerSortName } from '@sorare/core/src/contexts/config';
import { useGQLSearchContext } from '@sorare/core/src/contexts/graphql-search';
import { useI18nSortByItems } from '@sorare/core/src/hooks/search/useI18nSortByItems';
import { useMarketplaceLifecycle } from '@sorare/core/src/hooks/useMarketplaceLifecycle';
import { PlayerSearchViewAttributes } from '@sorare/core/src/lib/playerSearchView';

import type { PlayerResultsProps } from 'searchCommonPlayer/AdvancedPlayerSearch/types';
import { useManagePlayerSearchView } from 'searchCommonPlayer/StatsManager/useManagePlayerSearchView';

import { SearchPlayerRow } from '../SearchPlayerRow';
import { SearchPlayerRowSkeletons } from '../SearchPlayerRowSkeletons';
import { SearchPlayerTableHeader } from '../SearchPlayerTableHeader';
import { SearchTable } from '../SearchTable';
import type { BackendSearchPlayerTable_AnyPlayerInterface } from './__generated__/index.graphql';

type Hit = {
  anyPlayer: BackendSearchPlayerTable_AnyPlayerInterface;
};

export const SearchPlayerTable = ({
  indexes = [],
  onStatsClick,
  onListingsClick,
  watchlistId,
  favoritesWatchlist,
  defaultRowsNumber = 20,
}: PlayerResultsProps & {
  onStatsClick: (playerSlug: string) => void;
  onListingsClick: (playerSlug: string) => void;
  watchlistId?: string;
  favoritesWatchlist?: boolean;
  defaultRowsNumber?: number;
}) => {
  const sortItems = useI18nSortByItems(indexes);
  const { updateSort } = useMarketplaceLifecycle();

  const { refine, currentRefinement } = useSortBy({
    items: sortItems,
  });
  const { status } = useInstantSearch();
  const { items: commonPlayers, results } = useHits<Hit>();
  const loading = status === 'loading' || status === 'stalled';

  const { statsManager } = useManagePlayerSearchView();
  const rarity = statsManager[PlayerSearchViewAttributes.RARITY];

  const sorts = (currentRefinement?.split(',') ||
    []) as PrintablePlayerSortName[];

  const sortBy = (_sorts: PrintablePlayerSortName[]) => {
    updateSort(_sorts);
    refine(_sorts.join(','));
  };

  const rowsNumber =
    (watchlistId && results?.nbHits) ||
    ((results?.nbHits || 0) < defaultRowsNumber && results?.nbHits) ||
    20;
  return (
    <SearchTable
      header={<SearchPlayerTableHeader sorts={sorts} refine={sortBy} />}
    >
      {loading ? (
        <SearchPlayerRowSkeletons rowsNumber={rowsNumber} />
      ) : (
        commonPlayers.map(({ anyPlayer }) => {
          return (
            <SearchPlayerRow
              key={anyPlayer.slug}
              anyPlayer={anyPlayer}
              onStatsClick={() => onStatsClick(anyPlayer.slug)}
              onListingsClick={() => onListingsClick(anyPlayer.slug)}
              rarity={rarity}
              watchlistId={watchlistId}
              favoritesWatchlist={favoritesWatchlist}
            />
          );
        })
      )}
    </SearchTable>
  );
};

export const BackendSearchPlayerTable = ({
  items,
  loading,
  onStatsClick,
  onListingsClick,
  watchlistId,
  favoritesWatchlist,
  defaultRowsNumber = 20,
}: PlayerResultsProps & {
  items?: {
    anyPlayer: BackendSearchPlayerTable_AnyPlayerInterface;
  }[];
  loading: boolean;
  onStatsClick: (playerSlug: string) => void;
  onListingsClick: (playerSlug: string) => void;
  watchlistId?: string;
  favoritesWatchlist?: boolean;
  defaultRowsNumber?: number;
}) => {
  const { setSearchParams, sorts } = useGQLSearchContext()!;
  const { updateSort } = useMarketplaceLifecycle();

  const results = { nbHits: 50 };
  const commonPlayers = items ?? [];

  const { statsManager } = useManagePlayerSearchView();
  const rarity = statsManager[PlayerSearchViewAttributes.RARITY];

  const sortBy = (_sorts: PrintablePlayerSortName[]) => {
    updateSort(_sorts);
    setSearchParams({ s: _sorts.map(encodeURIComponent).join(',') });
  };

  const rowsNumber =
    (watchlistId && results?.nbHits) ||
    ((results?.nbHits || 0) < defaultRowsNumber && results?.nbHits) ||
    20;
  return (
    <SearchTable
      header={
        <SearchPlayerTableHeader
          sorts={sorts as PrintablePlayerSortName[]}
          refine={sortBy}
        />
      }
    >
      {loading ? (
        <SearchPlayerRowSkeletons rowsNumber={rowsNumber} />
      ) : (
        commonPlayers.map(({ anyPlayer }) => {
          return (
            <SearchPlayerRow
              key={anyPlayer.slug}
              anyPlayer={anyPlayer}
              onStatsClick={() => onStatsClick(anyPlayer.slug)}
              onListingsClick={() => onListingsClick(anyPlayer.slug)}
              rarity={rarity}
              watchlistId={watchlistId}
              favoritesWatchlist={favoritesWatchlist}
            />
          );
        })
      )}
    </SearchTable>
  );
};

BackendSearchPlayerTable.fragments = {
  AnyPlayerInterface: gql`
    fragment BackendSearchPlayerTable_AnyPlayerInterface on AnyPlayerInterface {
      slug
      ...SearchPlayerRow_anyPlayer
    }
    ${SearchPlayerRow.fragments.anyPlayer}
  ` as TypedDocumentNode<BackendSearchPlayerTable_AnyPlayerInterface>,
};
